import { supabase } from "@/integrations/supabase/client";

/**
 * Resolves the apartment ID for a user.
 * Checks users.apartment_id first, then falls back to user_apartments table
 * so cotenants (secondary tenants) see the same data as primary tenants.
 */
export async function getUserApartmentId(userId: string): Promise<string | null> {
  // 1. Check direct assignment on users table
  const { data: userData } = await supabase
    .from("users")
    .select("apartment_id")
    .eq("id", userId)
    .maybeSingle();

  if (userData?.apartment_id) {
    return userData.apartment_id;
  }

  // 2. Fallback: check user_apartments table (cotenant)
  const { data: ua } = await supabase
    .from("user_apartments")
    .select("apartment_id")
    .eq("user_id", userId)
    .limit(1)
    .maybeSingle();

  if (ua?.apartment_id) {
    return ua.apartment_id;
  }

  // 3. Fallback: check unit_owners table (owner role)
  const { data: uo } = await supabase
    .from("unit_owners")
    .select("apartment_id")
    .eq("user_id", userId)
    .limit(1)
    .maybeSingle();

  return uo?.apartment_id ?? null;
}

export type ApartmentMeterRow = {
  id: string;
  meter_number?: string | null;
  sts_meter_no?: string | null;
  compliance_type?: string | null;
  deveui?: string | null;
  overdraft_limit_kwh?: number | null;
  last_reading_at?: string | null;
  manufacturers?: { slug: string } | null;
};

/**
 * Resolve the smart meter for an apartment.
 * Prefer apartments.smart_meter_id when set; otherwise the meter with the
 * newest last_reading_at. Uses limit(1) so multi-meter apartments do not
 * trip PostgREST maybeSingle() errors.
 */
export async function getApartmentSmartMeter(
  apartmentId: string,
  select = "id, meter_number, sts_meter_no, compliance_type, deveui, overdraft_limit_kwh, last_reading_at, manufacturers(slug)",
): Promise<ApartmentMeterRow | null> {
  const { data: apartmentRow } = await supabase
    .from("apartments")
    .select("smart_meter_id")
    .eq("id", apartmentId)
    .maybeSingle();

  if (apartmentRow?.smart_meter_id) {
    const { data: byFk } = await supabase
      .from("smart_meters")
      .select(select)
      .eq("id", apartmentRow.smart_meter_id)
      .maybeSingle();
    if (byFk) return byFk as unknown as ApartmentMeterRow;
  }

  const { data: rows, error } = await supabase
    .from("smart_meters")
    .select(select)
    .eq("apartment_id", apartmentId)
    .order("last_reading_at", { ascending: false, nullsFirst: false })
    .limit(1);

  if (error) {
    console.error("[getApartmentSmartMeter]", error);
    return null;
  }

  return (rows?.[0] as unknown as ApartmentMeterRow) ?? null;
}
